import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { formatErr } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Users } from "@/components/icons";

/**
 * Login estilo minimalista tipo "App Store Connect / Apple ID".
 *   · Logo top-left absoluto
 *   · Icono grande centrado + título
 *   · Un solo input redondeado (email → password)
 *   · Nota de privacidad con icono + link
 *   · Botón "Continuar" (primary)
 *   · Footer bar con Copyright + Términos + Privacidad
 */
export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState("email"); // "email" | "password"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const emailRef = useRef(null);
  const pwdRef = useRef(null);

  useEffect(() => {
    (step === "email" ? emailRef : pwdRef).current?.focus();
  }, [step]);

  const toPassword = (e) => {
    e?.preventDefault();
    setErr("");
    const v = email.trim();
    if (!v || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      setErr("Ingresa un correo válido.");
      return;
    }
    setStep("password");
  };

  const submit = async (e) => {
    e?.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await login(email.trim(), password);
      if (u?.__needs_password_reset) {
        nav("/crear-contrasena", {
          replace: true,
          state: {
            reset_token: u.reset_token,
            email: u.email,
            full_name: u.full_name,
          },
        });
        return;
      }
      toast.success(`Bienvenido, ${u.name || u.full_name || u.email}`);
      const redir = {
        admin: "/admin",
        doctor: "/doctor",
        secretary: "/secretaria",
        pharmacy: "/farmacia",
      }[u.role];
      nav(redir, { replace: true });
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  const goingToPassword = step === "email";

  return (
    <div className="min-h-screen w-full bg-[#FAFAFC] flex flex-col relative">
      {/* ── Logo top-left absoluto ─────────────────────────────────── */}
      <div className="absolute top-4 left-6 sm:top-6 sm:left-8">
        <img
          src="/brand/consudoctor-icon.png"
          alt="ConsuDoctor"
          className="w-8 h-8 sm:w-10 sm:h-10 object-contain opacity-70"
        />
      </div>

      {/* ── Cuerpo centrado ────────────────────────────────────────── */}
      <div className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-[500px] auth-fade-slide">
          {/* Título — sin logo */}
          <div className="flex flex-col items-center gap-3 mb-8">
            <h1
              data-testid="login-title"
              className="text-[26px] sm:text-[30px] font-medium text-black tracking-tight"
            >
              ConsuDoctor
            </h1>
          </div>

          {/* Input pill */}
          <form
            onSubmit={goingToPassword ? toPassword : submit}
            className="space-y-4"
          >
            {goingToPassword ? (
              <PillInput
                type="email"
                value={email}
                onChange={setEmail}
                placeholder="Correo electrónico"
                inputRef={emailRef}
                testId="login-email-input"
                autoComplete="email"
              />
            ) : (
              <PillInput
                type="password"
                value={password}
                onChange={setPassword}
                placeholder={`Contraseña de ${email}`}
                inputRef={pwdRef}
                testId="login-password-input"
                autoComplete="current-password"
              />
            )}

            {/* Nota de privacidad con icono */}
            <div className="pt-1">
              <div className="w-8 h-8 mb-2 rounded-lg bg-[#0071E3]/[0.08] flex items-center justify-center">
                <Users
                  className="w-4 h-4 text-[#0071E3]"
                  strokeWidth={1.75}
                  weight="regular"
                />
              </div>
              <p className="text-[13px] leading-[1.55] text-black/60">
                Tu información de cuenta se usa para permitirte iniciar sesión
                de forma segura y acceder a tus pacientes, recetas y
                consultorio. ConsuDoctor guarda ciertos datos con fines
                clínicos, de soporte y de auditoría médica de acuerdo con la
                Ley 42-01.{" "}
                <Link
                  to="/privacidad"
                  className="text-[#0071E3] font-medium hover:underline underline-offset-4"
                  data-testid="login-privacy-link"
                >
                  Ver cómo se administran tus datos…
                </Link>
              </p>
            </div>

            {err && (
              <div
                data-testid="login-error"
                className="text-[13px] text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2"
              >
                {err}
              </div>
            )}

            {/* Botones */}
            <div className="flex flex-col sm:flex-row items-stretch gap-2 pt-2">
              <button
                type="submit"
                disabled={loading || (goingToPassword ? !email.trim() : !password)}
                data-testid={
                  goingToPassword ? "login-email-arrow" : "login-submit-button"
                }
                className="flex-1 h-11 rounded-lg bg-[#0071E3] text-white text-[14px] font-semibold hover:bg-[#0077ED] transition disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#0071E3] inline-flex items-center justify-center gap-2"
              >
                {loading ? (
                  <span className="w-4 h-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin" />
                ) : goingToPassword ? (
                  "Continuar"
                ) : (
                  "Iniciar sesión"
                )}
              </button>

              {goingToPassword ? (
                <Link
                  to="/registro"
                  data-testid="login-register-btn"
                  className="flex-1 h-11 rounded-lg bg-black hover:bg-black/85 text-white text-[14px] font-semibold inline-flex items-center justify-center transition"
                >
                  Registrarme
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setStep("email");
                    setErr("");
                    setPassword("");
                  }}
                  data-testid="login-back-btn"
                  className="sm:w-44 h-11 rounded-lg bg-white border border-black/10 hover:bg-black/[0.03] text-black text-[14px] font-medium inline-flex items-center justify-center gap-1.5 transition"
                >
                  <ArrowLeft
                    className="w-3.5 h-3.5"
                    strokeWidth={1.75}
                    weight="regular"
                  />
                  Cambiar correo
                </button>
              )}
            </div>

            {step === "password" && (
              <div className="text-center pt-2">
                <Link
                  to="/recuperar-contrasena"
                  data-testid="forgot-password-link"
                  className="text-[13px] text-black/60 hover:text-black font-medium transition"
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
            )}
          </form>

          {/* Nota de términos */}
          <div className="mt-10 pt-6 border-t border-black/[0.06]">
            <p className="text-center text-[12px] text-black/50 leading-[1.55]">
              Al crear una cuenta o iniciar sesión aceptas nuestros{" "}
              <Link
                to="/terminos"
                className="text-black/70 hover:text-black underline underline-offset-4"
              >
                Términos
              </Link>{" "}
              y confirmas haber leído la{" "}
              <Link
                to="/privacidad"
                className="text-black/70 hover:text-black underline underline-offset-4"
              >
                Política de Privacidad
              </Link>
              .
            </p>
          </div>
        </div>
      </div>

      {/* ── Footer bar ────────────────────────────────────────────── */}
      <footer
        data-testid="login-footer"
        className="w-full bg-[#F7F8FA] border-t border-black/[0.05]"
      >
        <div className="max-w-6xl mx-auto px-6 py-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-[11px] text-black/50">
          <span>
            Copyright © {new Date().getFullYear()} ConsuDoctor. Todos los
            derechos reservados.
          </span>
          <span className="text-black/20">|</span>
          <Link to="/terminos" className="hover:text-black transition">
            Términos de servicio
          </Link>
          <Link to="/privacidad" className="hover:text-black transition">
            Política de privacidad
          </Link>
          <Link to="/soporte" className="hover:text-black transition">
            Soporte
          </Link>
        </div>
      </footer>
    </div>
  );
}

// ── Input redondo tipo Apple ──────────────────────────────────────────
function PillInput({
  type = "text",
  value,
  onChange,
  placeholder,
  inputRef,
  testId,
  autoComplete,
}) {
  return (
    <input
      ref={inputRef}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testId}
      required
      autoComplete={autoComplete}
      className="w-full h-12 rounded-lg bg-white border border-black/15 focus:border-[#0071E3] focus:ring-4 focus:ring-[#0071E3]/15 px-4 text-[14px] text-black placeholder-black/40 outline-none transition"
    />
  );
}

// ── Compat: primitivos exportados usados por CreatePasswordPage y otros ──
export function AuthCard({ children }) {
  return <div className="w-full max-w-sm">{children}</div>;
}
export function FlatInput({
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  inputRef,
  testId,
  autoComplete,
  required = true,
}) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wider text-black/50 font-semibold mb-1.5 px-1">
        {label}
      </span>
      <input
        ref={inputRef}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        required={required}
        autoComplete={autoComplete}
        className="w-full bg-[#F2F3F5] hover:bg-[#EDEEF0] focus:bg-white border border-transparent focus:border-[#0071E3] focus:ring-4 focus:ring-[#0071E3]/15 rounded-xl px-4 py-3.5 text-black text-[15px] placeholder-black/30 outline-none transition-all"
      />
    </label>
  );
}
export function PrimaryButton({
  children,
  loading,
  testId,
  type = "button",
  onClick,
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={loading}
      data-testid={testId}
      className="w-full mt-2 bg-[#0071E3] hover:bg-[#0077ED] active:bg-[#0071E3] text-white rounded-xl py-3.5 text-[15px] font-semibold flex items-center justify-center gap-2 transition disabled:opacity-60 disabled:cursor-not-allowed"
    >
      {loading ? (
        <span className="w-4 h-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin" />
      ) : (
        children
      )}
    </button>
  );
}

export function Divider() {
  return (
    <div className="my-6 flex items-center gap-3">
      <div className="flex-1 h-px bg-black/10" />
      <div className="text-[11px] uppercase tracking-wider text-black/40 font-semibold">
        o
      </div>
      <div className="flex-1 h-px bg-black/10" />
    </div>
  );
}

export function SocialButton({ icon, label, disabled, badge, onClick, ...rest }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="w-full rounded-xl flex items-center gap-3 px-4 py-3 text-black transition bg-white hover:bg-[#F7F8F9] border border-black/10 disabled:opacity-70 disabled:cursor-not-allowed"
      {...rest}
    >
      <span className="shrink-0">{icon}</span>
      <span className="flex-1 text-left text-sm font-semibold">{label}</span>
      {badge && (
        <span className="text-[10px] font-bold uppercase tracking-wider text-black/50 bg-black/[0.05] px-2 py-1 rounded-full">
          {badge}
        </span>
      )}
    </button>
  );
}

export function GoogleG() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.75 3.28-8.09z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.99.66-2.25 1.06-3.72 1.06-2.86 0-5.29-1.93-6.15-4.53H2.18v2.84A11 11 0 0 0 12 23z" fill="#34A853" />
      <path d="M5.85 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.67-2.84z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.67 2.84C6.71 7.31 9.14 5.38 12 5.38z" fill="#EA4335" />
    </svg>
  );
}
